<template>
  <transition name="console-slide">
    <div v-if="isVisible" class="platform-console-overlay" @click.self="close">
      <div class="platform-console" :class="{ 'fullscreen': isFullscreen }">
        <!-- Header -->
        <div class="console-header">
          <div class="console-title">
            <i class="bi bi-terminal"></i>
            <span>{{ $t('console.title') }}</span>
            <span class="console-badge">Beta</span>
          </div>
          <div class="console-actions">
            <button 
              class="console-btn" 
              @click="toggleFullscreen"
              :title="isFullscreen ? $t('console.exitFullscreen') : $t('console.enterFullscreen')"
            >
              <i :class="isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'"></i>
            </button>
            <button class="console-btn" @click="clearConsole" :title="$t('console.clear')">
              <i class="bi bi-trash"></i>
            </button>
            <button class="console-btn" @click="close" :title="$t('console.close')">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <!-- Main Content -->
        <div class="console-content">
          <!-- Sidebar with History -->
          <div class="console-sidebar" v-if="!isFullscreen || showSidebar">
            <div class="sidebar-tabs">
              <button class="active">
                {{ $t('console.history') }}
              </button>
            </div>

            <!-- History Tab -->
            <div class="history-list">
              <div 
                v-for="(item, index) in commandHistory" 
                :key="index"
                class="history-item"
                @click="loadFromHistory(item)"
              >
                <div class="history-date">{{ formatDate(item.timestamp) }}</div>
                <div class="history-code">{{ item.code.substring(0, 100) }}...</div>
              </div>
              <div v-if="commandHistory.length === 0" class="empty-state">
                {{ $t('console.noHistory') }}
              </div>
            </div>
          </div>

          <!-- Main Editor Area -->
          <div class="console-main">
            <!-- Monaco Editor -->
            <div class="editor-wrapper">
              <monaco-editor
                v-model="code"
                language="javascript"
                :height="editorHeight"
                :options="editorOptions"
                :showFullscreenButton="false"
                :enableProtokolTypes="true"
                ref="monacoEditor"
              />
            </div>

            <!-- Action Bar -->
            <div class="console-action-bar">
              <div class="action-left">
                <button 
                  class="btn btn-sm btn-outline-secondary"
                  @click="toggleSidebar"
                  v-if="isFullscreen"
                >
                  <i :class="showSidebar ? 'bi bi-layout-sidebar-inset' : 'bi bi-layout-sidebar'"></i>
                  {{ showSidebar ? $t('console.hideSidebar') : $t('console.showSidebar') }}
                </button>
              </div>
              <div class="action-right">
                <button 
                  class="btn btn-sm btn-success"
                  @click="executeCode"
                  :disabled="isExecuting || !code.trim()"
                >
                  <i class="bi bi-play-fill"></i>
                  {{ $t('console.run') }}
                  <span class="kbd-hint">Ctrl+Enter</span>
                </button>
              </div>
            </div>

            <!-- Output Area -->
            <div class="console-output" ref="outputArea">
              <div class="output-header">
                <span>{{ $t('console.output') }}</span>
                <button 
                  class="btn btn-sm btn-link"
                  @click="clearOutput"
                  v-if="outputs.length > 0"
                >
                  {{ $t('console.clearOutput') }}
                </button>
              </div>
              <div class="output-content">
                <div 
                  v-for="(output, index) in outputs" 
                  :key="index"
                  class="output-entry"
                  :class="output.type"
                >
                  <div class="output-timestamp">{{ formatTime(output.timestamp) }}</div>
                  <div class="output-body">
                    <template v-if="output.type === 'result'">
                      <JsonTreeView 
                        v-if="isJsonData(output.data)"
                        :data="output.data"
                        :maxDepth="3"
                        :readOnly="true"
                      />
                      <pre v-else>{{ formatResult(output.data) }}</pre>
                    </template>
                    <pre v-else-if="output.type === 'error'" class="error-message">{{ formatErrorData(output.data) }}</pre>
                    <template v-else-if="output.type === 'log'">
                      <div 
                        v-for="(arg, argIndex) in output.data"
                        :key="argIndex" 
                        class="log-arg"
                      >
                        <JsonTreeView 
                          v-if="isJsonData(arg)"
                          :data="arg"
                          :maxDepth="3"
                          :readOnly="true"
                        />
                        <pre v-else>{{ formatResult(arg) }}</pre>
                      </div>
                    </template>
                  </div>
                </div>
                <div v-if="outputs.length === 0" class="empty-output">
                  {{ $t('console.noOutput') }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer with Help -->
        <div class="console-footer">
          <div class="help-text">
            <i class="bi bi-info-circle"></i>
            {{ $t('console.helpText') }}
            <a href="#" @click.prevent="showDocumentation">{{ $t('console.viewDocs') }}</a>
          </div>
          <div class="keyboard-shortcuts">
            <span class="shortcut-item"><kbd>Ctrl</kbd>+<kbd>Enter</kbd> {{ $t('console.runShortcut') }}</span>
            <span class="shortcut-item"><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>K</kbd> {{ $t('console.toggleShortcut') }}</span>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import MonacoEditor from '@/components/form/Editor/MonacoEditor'
import JsonTreeView from '@/components/elements/JsonTreeView'
import ConsoleRunner from './ConsoleRunner'
import * as SDK from '@ptkl/sdk'
import * as SDKBeta from '@ptkl/sdk/beta'
import moment from 'moment'
import _ from 'lodash'
import * as Babel from '@babel/standalone'

export default {
  name: 'PlatformConsole',
  components: {
    MonacoEditor,
    JsonTreeView
  },
  data() {
    return {
      code: '',
      outputs: [],
      commandHistory: [],
      isExecuting: false,
      isFullscreen: false,
      showSidebar: true,
      editorOptions: {
        automaticLayout: true,
        fontSize: 13,
        lineNumbers: 'on',
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        tabSize: 2,
      }
    }
  },
  computed: {
    isVisible() {
      const visible = this.$store.state.console.isVisible
      console.log('[PlatformConsole] isVisible computed:', visible)
      return visible
    },
    editorHeight() {
      if (this.isFullscreen) {
        return window.innerHeight - 250
      }
      return 300
    }
  },
  watch: {
    isVisible(newVal) {
      console.log('[PlatformConsole] isVisible changed to:', newVal)
      if (newVal) {
        this.loadHistoryFromStorage()
        this.$nextTick(() => {
          // Focus editor when console opens
          if (this.$refs.monacoEditor && this.$refs.monacoEditor.editor) {
            this.$refs.monacoEditor.editor.focus()
          }
        })
      }
    }
  },

  created() {
    console.log('[PlatformConsole] Component created')
    console.log('[PlatformConsole] Initial visibility:', this.$store.state.console)
  },
  methods: {
    close() {
      this.$store.commit('console/setVisible', false)
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
    },

    toggleSidebar() {
      this.showSidebar = !this.showSidebar
    },

    clearConsole() {
      this.code = ''
      this.clearOutput()
    },

    clearOutput() {
      this.outputs = []
    },

    loadFromHistory(item) {
      this.code = item.code
    },

    async executeCode() {
      if (!this.code.trim() || this.isExecuting) return

      this.isExecuting = true
      const startTime = Date.now()

      // Save to history
      this.saveToHistory(this.code)

      try {
        // Create SDK context
        const context = this.createExecutionContext()

        // Override console methods to capture logs
        const originalLog = console.log
        const originalError = console.error
        const logs = []

        console.log = (...args) => {
          logs.push({ type: 'log', data: args, timestamp: new Date() })
          originalLog(...args)
        }
        console.error = (...args) => {
          logs.push({ type: 'error', data: args, timestamp: new Date() })
          originalError(...args)
        }

        // Add require function to context for CommonJS modules
        const moduleCache = {}
        context.require = function(modulePath) {
          if (moduleCache[modulePath]) {
            return moduleCache[modulePath]
          }
          
          let moduleExports = {}
          
          if (modulePath === '@ptkl/sdk') {
            moduleExports = SDK
            moduleCache[modulePath] = moduleExports
          } else if (modulePath === '@ptkl/sdk/beta') {
            moduleExports = SDKBeta
            moduleCache[modulePath] = moduleExports
          } else if (modulePath === 'lodash') {
            moduleExports = _
            moduleCache[modulePath] = moduleExports
          } else if (modulePath === 'moment') {
            moduleExports = moment
            moduleCache[modulePath] = moduleExports
          } else {
            throw new Error(`Module not found: ${modulePath}. Available modules: @ptkl/sdk, @ptkl/sdk/beta, lodash, moment`)
          }
          
          return moduleExports
        }

        // Add module and exports for potential module.exports usage
        context.module = { exports: {} }
        context.exports = context.module.exports

        // Transform ESM to CommonJS with Babel
        let transformedCode = this.code

        originalLog('[Console] Original code:', this.code)
        
        // try {
        //   const babelResult = Babel.transform(this.code, {
        //     presets: [
        //       ['env', {
        //         modules: 'commonjs' // Transform ES6 modules to CommonJS
        //       }]
        //     ],
        //     plugins: [
        //       'transform-async-to-generator'
        //     ]
        //   })
        //   transformedCode = babelResult.code
        //   originalLog('[Console] Babel transformed code:', transformedCode)
        // } catch (babelError) {
        //   logs.push({ type: 'error', data: ['Babel transformation failed:', babelError.message], timestamp: new Date() })
        //   originalError('[Console] Babel transformation failed:', babelError.message)
        // }

        // Execute code with async support
        const runner = new ConsoleRunner()
        // Just pass the transformed code, runner will handle it
        const result = await runner.runJS(this.code, context, 'Platform Console')

        // Restore console
        console.log = originalLog
        console.error = originalError
        

        // Add logs to output
        logs.forEach(log => {
          this.outputs.push(log)
        })

        // Add result to output (skip empty objects or undefined)
        if (result !== undefined && !(typeof result === 'object' && result !== null && Object.keys(result).length === 0)) {
          this.outputs.push({
            type: 'result',
            data: result,
            timestamp: new Date(),
            executionTime: Date.now() - startTime
          })
        }

        // Auto-scroll to bottom
        this.$nextTick(() => {
          if (this.$refs.outputArea) {
            this.$refs.outputArea.scrollTop = this.$refs.outputArea.scrollHeight
          }
        })

      } catch (error) {
        console.error('Console execution error:', error)
        this.outputs.push({
          type: 'error',
          data: error.message || error.toString(),
          timestamp: new Date(),
          executionTime: Date.now() - startTime
        })
      } finally {
        this.isExecuting = false
      }
    },

    createExecutionContext() {
      return {
        // Legacy SDK helper for backwards compatibility
        $sdk: {
            version(version) {
                if (version === 'stable' || version === 'latest' || version === '0.9') return SDK
                if (version === 'beta' || version === '0.10') return SDKBeta
                // Default to stable
                return SDK
            }
        },
        
        // Utility
        moment,
        lodash: _,
        _,  // Also expose as underscore convention
        
        // Console methods
        console: {
          log: (...args) => console.log(...args),
          error: (...args) => console.error(...args),
          warn: (...args) => console.warn(...args),
          info: (...args) => console.info(...args),
        },

        exports: {}
      }
    },

    isJsonData(data) {
      // Check if data should be rendered as JSON tree
      return data !== null && 
             data !== undefined && 
             typeof data === 'object' && 
             !(data instanceof Error)
    },

    formatErrorData(data) {
      // Format error data
      if (Array.isArray(data)) {
        return data.map(item => {
          if (item instanceof Error) {
            return item.stack || item.message
          }
          return typeof item === 'object' ? JSON.stringify(item, null, 2) : String(item)
        }).join('\n')
      }
      if (data instanceof Error) {
        return data.stack || data.message
      }
      return typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data)
    },

    formatResult(data) {
      if (typeof data === 'undefined') return 'undefined'
      if (data === null) return 'null'
      if (typeof data === 'string') return data
      if (typeof data === 'number' || typeof data === 'boolean') return String(data)
      return JSON.stringify(data, null, 2)
    },

    formatDate(date) {
      return moment(date).format('MMM D, YYYY HH:mm:ss')
    },

    formatTime(date) {
      return moment(date).format('HH:mm:ss.SSS')
    },

    saveToHistory(code) {
      const historyItem = {
        code,
        timestamp: new Date()
      }
      
      this.commandHistory.unshift(historyItem)
      
      // Keep only last 50 items
      if (this.commandHistory.length > 50) {
        this.commandHistory = this.commandHistory.slice(0, 50)
      }

      // Save to localStorage
      try {
        localStorage.setItem('platform_console_history', JSON.stringify(this.commandHistory))
      } catch (e) {
        console.error('Failed to save console history:', e)
      }
    },

    loadHistoryFromStorage() {
      try {
        const stored = localStorage.getItem('platform_console_history')
        if (stored) {
          this.commandHistory = JSON.parse(stored)
        }
      } catch (e) {
        console.error('Failed to load console history:', e)
      }
    },

    showDocumentation() {
      window.open('https://docs.protokol.io', '_blank')
    },

    handleKeydown(e) {
      // Ctrl/Cmd + Enter to execute
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        this.executeCode()
      }
    }
  },

  mounted() {
    console.log('[PlatformConsole] Component mounted')
    console.log('[PlatformConsole] Store available:', !!this.$store)
    console.log('[PlatformConsole] Console module in store:', this.$store.state.console)
    
    // Load history on mount
    this.loadHistoryFromStorage()

    // Add keydown listener for Ctrl+Enter
    document.addEventListener('keydown', this.handleKeydown)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
  }
}
</script>

<style scoped lang="scss">
.platform-console-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.platform-console {
  background: var(--bg-main-end, #1e1e1e);
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  width: 90%;
  max-width: 1400px;
  height: 80vh;
  max-height: 900px;
  overflow: hidden;

  &.fullscreen {
    width: 100%;
    height: 100vh;
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
}

.console-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #333;
  background: #252526;
}

.console-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #fff;

  i {
    font-size: 18px;
  }
}

.console-badge {
  font-size: 10px;
  padding: 2px 6px;
  background: #0098ff;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: 600;
}

.console-actions {
  display: flex;
  gap: 8px;
}

.console-btn {
  background: transparent;
  border: none;
  color: #ccc;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #333;
    color: #fff;
  }

  i {
    font-size: 16px;
  }
}

.console-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.console-sidebar {
  width: 280px;
  border-right: 1px solid #333;
  display: flex;
  flex-direction: column;
  background: #252526;
}

.sidebar-tabs {
  display: flex;
  border-bottom: 1px solid #333;

  button {
    flex: 1;
    padding: 10px;
    background: transparent;
    border: none;
    color: #ccc;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.2s;

    &:hover {
      background: #2a2a2a;
      color: #fff;
    }

    &.active {
      background: #1e1e1e;
      color: #fff;
      border-bottom: 2px solid #0098ff;
    }
  }
}

.examples-list,
.history-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.example-item {
  padding: 12px;
  margin-bottom: 8px;
  background: #2d2d2d;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #333;
    transform: translateX(2px);
  }
}

.example-title {
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
  font-size: 13px;
}

.example-description {
  font-size: 12px;
  color: #999;
}

.history-item {
  padding: 10px;
  margin-bottom: 6px;
  background: #2d2d2d;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #333;
  }
}

.history-date {
  font-size: 10px;
  color: #666;
  margin-bottom: 4px;
}

.history-code {
  font-size: 11px;
  color: #ccc;
  font-family: 'Monaco', 'Menlo', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.console-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-wrapper {
  flex: 0 0 auto;
}

.console-action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #333;
  border-bottom: 1px solid #333;
  background: #252526;
}

.kbd-hint {
  margin-left: 8px;
  font-size: 11px;
  opacity: 0.7;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.console-output {
  flex: 1;
  overflow-y: auto;
  background: #1e1e1e;
}

.output-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid #333;
  background: #252526;
  color: #ccc;
  font-size: 13px;
  font-weight: 600;
}

.output-content {
  padding: 12px;
}

.output-entry {
  margin-bottom: 12px;
  padding: 12px;
  background: #252526;
  border-radius: 6px;
  border-left: 3px solid #0098ff;

  &.error {
    border-left-color: #f44336;
  }

  &.log {
    border-left-color: #4caf50;
  }
}

.output-timestamp {
  font-size: 10px;
  color: #666;
  margin-bottom: 6px;
}

.output-body {
  pre {
    margin: 0;
    padding: 0;
    color: #ccc;
    font-size: 12px;
    font-family: 'Monaco', 'Menlo', monospace;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .error-message {
    color: #f44336;
  }

  .log-arg {
    display: inline-block;
    margin-right: 8px;
    
    &:last-child {
      margin-right: 0;
    }
  }
}

.empty-output,
.empty-state {
  text-align: center;
  color: #666;
  padding: 40px;
  font-size: 14px;
}

.console-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid #333;
  background: #252526;
  font-size: 12px;
  color: #999;
}

.help-text {
  display: flex;
  align-items: center;
  gap: 6px;

  a {
    color: #0098ff;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.keyboard-shortcuts {
  display: flex;
  gap: 16px;
}

.shortcut-item {
  display: flex;
  align-items: center;
  gap: 4px;

  kbd {
    padding: 2px 6px;
    background: #333;
    border-radius: 3px;
    font-size: 11px;
    border: 1px solid #555;
  }
}

// Animations
.console-slide-enter-active,
.console-slide-leave-active {
  transition: opacity 0.3s;

  .platform-console {
    transition: transform 0.3s;
  }
}

.console-slide-enter,
.console-slide-leave-to {
  opacity: 0;

  .platform-console {
    transform: scale(0.95) translateY(-20px);
  }
}

// Scrollbar styling
.console-sidebar,
.console-output {
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1e1e1e;
  }

  &::-webkit-scrollbar-thumb {
    background: #555;
    border-radius: 4px;

    &:hover {
      background: #666;
    }
  }
}
</style>
